/**
 * Admin CRUD schemas for the WhatsApp templates registry.
 * Source of truth: docs/architecture/database-schema.md §4.5 + Story 2.3.
 *
 * **Status flow** (enforced server-side, not in TypeBox):
 *   DRAFT       → POST submit → PENDING_META
 *   PENDING_META → POST sync   → APPROVED | REJECTED
 *   APPROVED    → POST disable → DISABLED
 *   REJECTED    → PATCH        → DRAFT (re-edit cycle)
 *
 * **No-Invention note:** the underlying `template_status` enum (database-schema.md
 * §3) was extended in migration 0002 with `draft`, `pending_meta`, `disabled`. The
 * legacy `pending`, `approved`, `rejected`, `paused` values remain for forward-
 * compat but the API only writes the new lifecycle values.
 */
import { Type } from '@sinclair/typebox';
/**
 * Public-facing status values surfaced through the admin API.
 * The DB enum is lower_snake_case; we expose UPPER for UX clarity (matches PM spec).
 */
export const TemplateStatus = Type.Union([
    Type.Literal('DRAFT'),
    Type.Literal('PENDING_META'),
    Type.Literal('APPROVED'),
    Type.Literal('REJECTED'),
    Type.Literal('DISABLED'),
], { $id: 'TemplateStatus' });
export const TEMPLATE_STATUSES = [
    'DRAFT',
    'PENDING_META',
    'APPROVED',
    'REJECTED',
    'DISABLED',
];
export const TemplateCategory = Type.Union([
    Type.Literal('UTILITY'),
    Type.Literal('MARKETING'),
    Type.Literal('AUTHENTICATION'),
], { $id: 'TemplateCategory' });
export const TEMPLATE_CATEGORIES = [
    'UTILITY',
    'MARKETING',
    'AUTHENTICATION',
];
export const TemplateLanguage = Type.Union([Type.Literal('pt_BR'), Type.Literal('pt_PT'), Type.Literal('en_US')], { $id: 'TemplateLanguage' });
/**
 * Meta restricts template names to lowercase letters, digits and underscore,
 * length 3..512. The same regex is enforced at the DB layer via index name.
 */
export const TEMPLATE_NAME_PATTERN = '^[a-z0-9_]{3,512}$';
/**
 * Body length cap mirrors Meta's 1024-char hard limit on HSM body components.
 */
export const TEMPLATE_BODY_MAX = 1024;
/**
 * Placeholder regex — `{{1}}, {{2}}, ...` strictly sequential starting at 1.
 *
 * The pattern check below is structural; the route handler additionally calls
 * `validatePlaceholders(body)` to enforce sequential numbering (1..N with no
 * gaps), since TypeBox cannot express that constraint declaratively.
 */
export const TEMPLATE_PLACEHOLDER_REGEX = /\{\{(\d+)\}\}/g;
/**
 * Validate placeholders are sequential `{{1}}, {{2}}, …` with no gaps and no
 * `{{0}}`. Returns `null` on success or a human-readable reason.
 */
export function validatePlaceholders(body) {
    const indices = [];
    // Reset lastIndex on the shared regex (global flag carries state).
    TEMPLATE_PLACEHOLDER_REGEX.lastIndex = 0;
    let match;
    while ((match = TEMPLATE_PLACEHOLDER_REGEX.exec(body)) !== null) {
        indices.push(Number(match[1]));
    }
    if (indices.length === 0)
        return null;
    const sorted = [...new Set(indices)].sort((a, b) => a - b);
    if (sorted[0] !== 1) {
        return 'Placeholders devem começar em {{1}}.';
    }
    for (let i = 0; i < sorted.length; i += 1) {
        if (sorted[i] !== i + 1) {
            return `Placeholders devem ser sequenciais (esperado {{${i + 1}}}, encontrado {{${sorted[i]}}}).`;
        }
    }
    return null;
}
/**
 * `TemplateButton` (2026-07-21, Task #92 — "template com botão") — a single
 * dynamic URL button on a template. `templates.buttons` (jsonb, migration
 * 0044) already existed and was consumed at SEND time by
 * `confirmation-scheduler.worker.ts`'s `buildUrlButtonComponent` (C4 only),
 * but nothing let an admin actually CREATE a template with one — the column
 * was only ever populated by hand for templates built directly in Meta
 * Business Manager. This is the first schema surfacing it for CRUD.
 *
 * `urlBase` is the STATIC prefix; the full Meta submission URL is always
 * `${urlBase}{{1}}` — a dynamic suffix filled in per-send (e.g. the actual
 * meeting link path). A purely static URL button (no dynamic part) is out
 * of scope: no consumer in this codebase needs one, and inventing that mode
 * without a real use case would violate Article IV.
 *
 * Capped at 1 button: `buildUrlButtonComponent` only ever looks up the
 * FIRST url-type button (`buttons.find(...)`) — allowing more here would
 * silently create buttons the send path can never reach.
 */
export const TemplateButton = Type.Object({
    type: Type.Literal('url'),
    index: Type.Integer({ minimum: 0, maximum: 0 }),
    /** Visible button label. Meta caps URL button text at 25 chars. */
    text: Type.String({ minLength: 1, maxLength: 25 }),
    urlBase: Type.String({ minLength: 1, maxLength: 2000 }),
});
/**
 * `Template` — full read-side projection returned by GET endpoints.
 */
export const Template = Type.Object({
    id: Type.String({ format: 'uuid' }),
    name: Type.String({ pattern: TEMPLATE_NAME_PATTERN }),
    language: TemplateLanguage,
    category: TemplateCategory,
    status: TemplateStatus,
    body: Type.String({ minLength: 1, maxLength: TEMPLATE_BODY_MAX }),
    variables: Type.Array(Type.String()),
    buttons: Type.Array(TemplateButton),
    metaTemplateId: Type.Union([Type.String(), Type.Null()]),
    rejectionReason: Type.Union([Type.String(), Type.Null()]),
    active: Type.Boolean(),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
/**
 * `TemplateCreate` — payload for `POST /admin/templates` (admin/manager).
 * Server inserts with status=DRAFT regardless of any client hint.
 */
export const TemplateCreate = Type.Object({
    name: Type.String({ pattern: TEMPLATE_NAME_PATTERN }),
    language: TemplateLanguage,
    category: TemplateCategory,
    body: Type.String({ minLength: 1, maxLength: TEMPLATE_BODY_MAX }),
    variables: Type.Optional(Type.Array(Type.String())),
    buttons: Type.Optional(Type.Array(TemplateButton, { maxItems: 1 })),
});
/**
 * `TemplateUpdate` — payload for `PATCH /admin/templates/:id`.
 * All fields optional; the route enforces that updates are only allowed when
 * the current status is DRAFT, REJECTED, or APPROVED (2026-07-21 — see
 * Story 8.6; immutable only while PENDING_META).
 */
export const TemplateUpdate = Type.Object({
    language: Type.Optional(TemplateLanguage),
    category: Type.Optional(TemplateCategory),
    body: Type.Optional(Type.String({ minLength: 1, maxLength: TEMPLATE_BODY_MAX })),
    variables: Type.Optional(Type.Array(Type.String())),
    buttons: Type.Optional(Type.Array(TemplateButton, { maxItems: 1 })),
});
/**
 * `TemplateListQuery` — query string of GET /admin/templates.
 * `status` accepts a CSV (e.g. `?status=DRAFT,APPROVED`) — splitting is the
 * route's responsibility because TypeBox + Fastify do not coerce arrays in
 * query strings reliably.
 */
export const TemplateListQuery = Type.Object({
    status: Type.Optional(Type.String()),
    category: Type.Optional(TemplateCategory),
    search: Type.Optional(Type.String()),
});
export const TemplateListResponse = Type.Object({
    templates: Type.Array(Template),
});
export const TemplateResponse = Type.Object({
    template: Template,
    /**
     * Mensagem de erro da Meta quando o envio automático (no salvar) falha. O
     * template é salvo localmente mesmo assim (fica DRAFT) — a UI mostra este
     * texto pro operador ajustar e salvar de novo.
     */
    metaError: Type.Optional(Type.String()),
    /**
     * QA review fix (2026-07-21) — nomes das campanhas ATIVAS que referenciam
     * este template, presente só quando editar um template `APPROVED` em uso
     * o resetou pra DRAFT + reenviou à Meta. Enquanto a Meta não reaprovar,
     * `lead-new.worker.ts` trata o template como indisponível e NENHUM lead
     * novo dessas campanhas recebe a abertura — a UI deve mostrar isso com
     * destaque, não como um detalhe secundário.
     */
    activeCampaignsWarning: Type.Optional(Type.Array(Type.String())),
});
/**
 * `TemplateSubmitResponse` — body of POST /admin/templates/:id/submit.
 */
export const TemplateSubmitResponse = Type.Object({
    template: Template,
    metaSubmissionId: Type.Optional(Type.String()),
});
/**
 * `TemplateSyncResponse` — body of POST /admin/templates/sync.
 */
export const TemplateSyncResponse = Type.Object({
    synced: Type.Integer({ minimum: 0 }),
    updated: Type.Integer({ minimum: 0 }),
    imported: Type.Integer({ minimum: 0 }),
    skipped: Type.Integer({ minimum: 0 }),
    categoryChanged: Type.Integer({ minimum: 0 }),
    categoryChanges: Type.Array(Type.Object({
        name: Type.String(),
        language: Type.String(),
        from: Type.String(),
        to: Type.String(),
    })),
});
//# sourceMappingURL=templates.js.map